import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import FileButton from './FileButton';
import SpeciesReferences from './SpeciesReferences';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import confidenceOptions from '../utils/confidenceOptions';
import uploadFile from '../utils/uploadFile';

// Mirrors champis-species' own SpeciesAutocompleteResult (js/src/common/
// speciesTypes.ts) - duplicated rather than imported, same as every other
// cross-extension shape in this codebase, since there's no build-time
// dependency on champis-species to import through. Kept to just the fields
// selectCandidate() below actually reads.

// Mirrors champis-species' own speciesName() (js/src/forum/utils/
// speciesName.tsx) real signature, narrowed to the one shape this file ever
// calls it with (SpeciesCandidate structurally satisfies that function's
// own SpeciesNameInput parameter - every field it reads is optional).

// A static `import ... from 'ext:champis-species/...'` captures whatever
// flarum.reg holds at *this module's own* evaluation time, which isn't
// guaranteed to be after champis-species has finished registering its own
// exports (both extensions' JS end up concatenated into one forum.js with
// no guaranteed top-level eval order) - confirmed the hard way in
// champis-forum's DiscussionFilters.js. Resolved lazily instead, at actual
// render time, same as that fix.
//
// Typed as `typeof Modal` (any real Component subclass works as a JSX tag
// type - Modal is already imported above) rather than Mithril.ComponentTypes:
// a bare ComponentTypes union has no construct/call signature TypeScript's
// own JSX checker recognizes, so `<SpeciesSearchWidget />` below fails to
// compile with it (confirmed directly - "does not have any construct or
// call signatures").
function getSpeciesSearchWidget() {
  return window.flarum.reg.get('champis-species', 'forum/components/SpeciesSearchWidget');
}
function getSpeciesName() {
  return window.flarum.reg.get('champis-species', 'forum/utils/speciesName');
}
/**
 * Logs (or edits) one species finding at an exhibition - attrs.exhibition
 * (required) + attrs.finding (existing model, switches to edit mode).
 * Species/confidence/photo/notes, same create/edit-in-one-component shape
 * as ExhibitionEditModal/champis-events' EventAssignModal.
 *
 * Species picking is a real 3-step flow, not a single autocomplete pick -
 * structural port of mycocarto's own ExhibitionSpecies.jsx (see
 * /Users/feliciencorbat/Development/champis-intranet/src/extensions/mycocarto/
 * Resources/Private/JavaScript/Exhibition/components/ExhibitionSpecies.jsx):
 * 1. search (SpeciesSearchWidget, a plain rough text match - whatever the
 *    user clicks here, accepted or synonym, is just a starting point);
 * 2. this.candidates - every synonym of *that* taxon plus its own accepted
 *    name, each annotated server-side (SpeciesCandidatesResource) with
 *    whether it's already a cartothèque card for this exhibition's société
 *    (`no`/`cardExists`) and whether it's already logged at this exact
 *    exhibition (`alreadyLogged`, disables re-adding it) - cartothèque
 *    matches sorted first, then the accepted name, then the rest;
 * 3. the actual pick from that list finalizes gbifId/speciesName.
 * See selectCandidate()/finalizeSpecies() below.
 */
export default class ExhibitionFindingEditModal extends Modal {
  undetermined = false;
  gbifId = null;
  speciesName = null;
  candidates = null; // step 2 list, see class docblock
  candidatesLoading = false;
  searchedGbifId = null; // which step-2 card was the actual step-1 pick, see selectCandidate()
  cfId = 0;
  label = '';
  savedLabel = ''; // the number this finding already has, see finalizeSpecies()
  vernacularName = '';
  labelIsAuto = true;
  vernacularNameIsAuto = true;
  notes = '';
  photos = [];
  photoUploading = false;
  loading = false;
  errorMessage = null;
  oninit(vnode) {
    super.oninit(vnode);
    const finding = this.attrs.finding;
    const display = finding && finding.speciesDisplay() || null;
    this.undetermined = finding ? finding.gbifId() === 0 : false;
    this.gbifId = finding && finding.gbifId() || null;
    this.speciesName = display ? display.canonicalName || display.scientificName : null;
    this.candidates = null; // step 2 list, see class docblock
    this.candidatesLoading = false;
    this.searchedGbifId = null; // which step-2 card was the actual step-1 pick, see selectCandidate()
    this.cfId = finding && finding.cfId() || 0;
    this.label = finding && finding.label() || '';
    this.savedLabel = this.label;
    this.vernacularName = finding && finding.vernacularName() || '';
    // Whether label/vernacularName still hold a value finalizeSpecies()
    // itself filled in (safe to replace when a *different* species is
    // picked via "changer") rather than one the user actually typed (see
    // the label/vernacularName fields' own oninput below, which flip
    // these to false - never flipped back, even for an existing finding's
    // own saved values, so picking a different species while editing
    // refreshes them exactly like a fresh finding does, unless retyped in
    // this same session).
    this.labelIsAuto = true;
    this.vernacularNameIsAuto = true;
    this.notes = finding && finding.notes() || '';
    // [{url, type}, ...] - soft cap of 5, matching mycocarto's own
    // maxitems: 5 (not enforced server-side, same "trust the client for UX
    // affordances" stance as the rest of this extension). Cloned rather
    // than aliased to finding.photos()'s own array - this.photos gets
    // mutated in place by add/removePhoto below, which would otherwise
    // silently mutate the model's own cached attribute value too.
    this.photos = finding ? (finding.photos() || []).map(photo => ({
      ...photo
    })) : [];
    this.photoUploading = false;
    this.loading = false;
    this.errorMessage = null;
  }

  /**
   * Step 1 -> step 2: the user just picked a rough match out of
   * SpeciesSearchWidget's own text-search results. SpeciesCandidatesResource
   * resolves it to its accepted name and every synonym of that same taxon
   * regardless of whether the clicked id was itself already a synonym, so
   * the actual pick in step 3 is made from the *complete*, cartothèque/
   * duplicate-annotated name set - not just whichever single record
   * happened to match the typed text. The accepted name always renders
   * first/bold in that grid (see content()), which reads as a "default"
   * pick and made it easy to click straight past whatever the user had
   * actually just searched for - this.searchedGbifId remembers *that* id
   * so the matching card can be flagged with its own "espèce recherchée"
   * badge. Used to also scroll that card into view automatically, back
   * when this grid had its own internal capped/scrollable region - removed
   * along with that region (see -candidatesScroll's own comment in
   * forum.less): the list opening already scrolled down to wherever the
   * searched card happened to sit, rather than showing its own top first,
   * per explicit feedback.
   */
  selectCandidate(species) {
    this.candidatesLoading = true;
    // species.id is a JSON:API resource id, always a string - candidate.gbifId
    // (below) comes through Schema\Integer, so this needs coercing or the
    // === comparison in content() silently never matches.
    this.searchedGbifId = Number(species.id);
    m.redraw();
    const filter = {
      gbifId: species.id,
      exhibition: this.attrs.exhibition.id()
    };
    if (this.attrs.finding) filter.excludeFinding = this.attrs.finding.id();
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/champis-exhibitions-species-candidates',
      params: {
        filter
      }
    }).then(response => {
      this.candidatesLoading = false;
      this.candidates = (response.data || []).map(item => item.attributes);
      m.redraw();
    }).catch(() => {
      // Best-effort - fall back to finalizing the rough match directly
      // rather than leaving the user stuck with no way to proceed.
      this.candidatesLoading = false;
      this.finalizeSpecies({
        gbifId: Number(species.id),
        canonicalName: species.canonicalName,
        scientificName: species.scientificName
      });
    });
  }

  /** Step 3: the actual final pick, from this.candidates. */
  finalizeSpecies(species) {
    this.gbifId = species.gbifId;
    this.speciesName = species.canonicalName || species.scientificName || null;
    this.candidates = null;

    // Convenience prefill, refreshed on every (re-)pick as long as the
    // user hasn't typed into the field themselves since (see labelIsAuto's
    // own declaration, and the label field's own oninput below) - this
    // used to only ever fill a *blank* field, so picking a different
    // species after already picking one silently kept the first one's own
    // number, confirmed directly as a real bug. A card-less species goes
    // back to the number the finding already had - never a previous pick's,
    // but one given while it was still undetermined stays.
    if (this.labelIsAuto) {
      this.label = species.no || this.savedLabel;
    }

    // Same refresh-unless-user-edited rule for the vernacular name - the
    // société's own cartothèque card takes priority over the live GBIF
    // lookup below when a card exists for this species, matching the
    // server-side ExhibitionFindingResource::creating() fallback order.
    if (this.vernacularNameIsAuto) {
      this.vernacularName = species.cardVernacularName || '';
    }

    // Live prefill, only if still auto and still empty after the above (no
    // card, or a card with no vernacular name of its own) - same rule (and
    // same SpeciesResource vernacularName opt-in field) as
    // CartothequeEntryEditModal's own selectSpecies(); never overwrites a
    // value the user already typed/edited, matching the server-side
    // ExhibitionFindingResource::creating() fallback.
    if (this.vernacularNameIsAuto && !this.vernacularName) {
      app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/species/' + species.gbifId,
        params: {
          vernacularName: 'true'
        }
      }).then(response => {
        // Bails if the user has since typed their own value, or a later
        // pick's own (synchronous) prefill already filled it - same
        // "don't clobber" guard as above, re-checked here since this
        // callback can land well after either has happened.
        if (!this.vernacularNameIsAuto || this.vernacularName) return;
        const vernacularName = response.data && response.data.attributes && response.data.attributes.vernacularName;
        if (vernacularName) {
          this.vernacularName = vernacularName;
          m.redraw();
        }
      }).catch(() => {
        // Best-effort prefill only - species selection itself already
        // succeeded, and ExhibitionFindingResource::creating() still
        // applies its own server-side fallback on save.
      });
    }
    m.redraw();
  }
  backToSearch() {
    this.gbifId = null;
    this.speciesName = null;
    this.candidates = null;
    this.candidatesLoading = false;
    this.searchedGbifId = null;
  }

  // speciesName (from getSpeciesName() below) can momentarily be undefined
  // on the same cross-extension load-order race SpeciesSearchWidget already
  // guards against a few lines above it in content() (see this file's own
  // top-of-file comment) - falls back to the plain, unitalicized name
  // rather than crashing on an undefined call. Was called unconditionally
  // before this had any real type checking on it at all.
  renderCandidateName(candidate, speciesNameFn) {
    return speciesNameFn ? speciesNameFn(candidate) : candidate.canonicalName || candidate.scientificName;
  }
  className() {
    return 'Modal--medium ChampisExhibitionFindingEditModal';
  }
  title() {
    const t = key => app.translator.trans(`champis-exhibitions.forum.finding_modal.${key}`);
    return this.attrs.finding ? t('edit_title') : t('create_title');
  }
  content() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.finding_modal.${key}`, params);
    };
    const SpeciesSearchWidget = getSpeciesSearchWidget();
    const speciesName = getSpeciesName();
    return m("div", {
      className: "Modal-body"
    }, this.errorMessage && m("div", {
      className: "ChampisExhibitionFindingEditModal-error"
    }, this.errorMessage), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", {
      className: "ChampisExhibitionFindingEditModal-undetermined"
    }, m("input", {
      type: "checkbox",
      checked: this.undetermined,
      onchange: e => {
        this.undetermined = e.target.checked;
        if (this.undetermined) {
          this.backToSearch();
        }
      }
    }), ' ', t('undetermined_label'))), !this.undetermined && m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('species_label')), this.speciesName ? m("div", {
      className: "ChampisExhibitionFindingEditModal-selectedSpecies"
    }, m("em", null, this.speciesName), m("a", {
      onclick: () => this.backToSearch()
    }, t('change_species'))) : this.candidatesLoading ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : this.candidates ? m("div", null, m("div", {
      className: "ChampisExhibitionFindingEditModal-candidatesScroll"
    }, m("div", {
      className: "ChampisExhibitionFindingEditModal-candidates"
    }, this.candidates.map(candidate => m("div", {
      key: candidate.gbifId,
      className: 'ChampisExhibitionFindingEditModal-candidate' + (
      // Green (a cartothèque card exists) takes priority
      // over blue (this is the accepted name) when both
      // apply - "espèce recherchée" no longer gets a
      // border of its own, just its text label below.
      candidate.cardExists ? ' ChampisExhibitionFindingEditModal-candidate--cartotheque' : candidate.accepted ? ' ChampisExhibitionFindingEditModal-candidate--accepted' : '')
    }, candidate.gbifId === this.searchedGbifId && m("div", {
      className: "ChampisExhibitionFindingEditModal-candidateSearched"
    }, t('searched_label')), candidate.no && m("div", {
      className: "ChampisExhibitionFindingEditModal-candidateNo"
    }, candidate.no), m("div", {
      className: "ChampisExhibitionFindingEditModal-candidateName"
    }, candidate.accepted ? m("strong", null, this.renderCandidateName(candidate, speciesName)) : this.renderCandidateName(candidate, speciesName)), m(Button, {
      className: "Button Button--primary",
      disabled: candidate.alreadyLogged,
      onclick: () => this.finalizeSpecies(candidate)
    }, candidate.alreadyLogged ? t('already_logged') : t('add_species_button')))))), m("a", {
      onclick: () => this.backToSearch()
    }, t('change_species'))) : SpeciesSearchWidget ? m(SpeciesSearchWidget, {
      placeholder: t('species_placeholder'),
      label: t('species_label'),
      hideLabel: true
      // Comma-separated species-group keys straight through to
      // species-autocomplete's own highertaxon filter (see
      // SpeciesAutocompleteService::resolveHigherTaxonQuery()) -
      // this exhibition's own configured restriction, empty
      // string (unrestricted) included, needs no translation at
      // this boundary since both sides already speak the exact
      // same comma-separated shape.
      ,
      highertaxon: this.attrs.exhibition.speciesGroups(),
      onselect: species => this.selectCandidate(species)
    }) : null, this.gbifId && m(SpeciesReferences, {
      gbifId: this.gbifId,
      name: this.speciesName
    })), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('confidence_label'), m("select", {
      className: "FormControl",
      value: this.cfId,
      onchange: e => {
        this.cfId = parseInt(e.target.value, 10);
      }
    }, confidenceOptions().map(o => m("option", {
      value: o.cfId
    }, o.label))))), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('label_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('label_placeholder'),
      value: this.label,
      oninput: e => {
        this.label = e.target.value;
        this.labelIsAuto = false;
      }
    }))), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('vernacular_name_label'), m("input", {
      className: "FormControl",
      type: "text",
      placeholder: t('vernacular_name_placeholder'),
      value: this.vernacularName,
      oninput: e => {
        this.vernacularName = e.target.value;
        this.vernacularNameIsAuto = false;
      }
    }))), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('photo_label')), this.photos.length > 0 && m("div", {
      className: "ChampisExhibitionFindingEditModal-photos"
    }, this.photos.map((photo, index) => m("div", {
      key: photo.url,
      className: "ChampisExhibitionFindingEditModal-photo"
    }, m("a", {
      href: photo.url,
      target: "_blank",
      rel: "noopener noreferrer"
    }, m("img", {
      className: "ChampisExhibitionFindingEditModal-photoThumb",
      src: photo.thumbnailUrl || photo.url,
      alt: ""
    })), m(Button, {
      className: "Button Button--link",
      onclick: () => this.removePhoto(index)
    }, t('photo_remove'))))), this.photos.length < 5 && m("div", {
      className: "ChampisExhibitionFindingEditModal-photoUpload"
    }, m(FileButton, {
      accept: "image/*",
      icon: "fas fa-camera",
      disabled: this.photoUploading,
      onfile: file => this.uploadPhoto(file)
    }, t('photo_choose_button')), this.photoUploading && m("span", {
      className: "ChampisExhibitionFindingEditModal-uploading"
    }, t('photo_uploading')))), m("div", {
      className: "ChampisExhibitionFindingEditModal-field"
    }, m("label", null, t('notes_label'), m("textarea", {
      className: "FormControl",
      placeholder: t('notes_placeholder'),
      value: this.notes,
      oninput: e => {
        this.notes = e.target.value;
      }
    }))), m("div", {
      className: "ChampisExhibitionFindingEditModal-footer"
    }, m(Button, {
      className: "Button Button--primary",
      onclick: () => this.submit(),
      loading: this.loading,
      disabled: !this.canSubmit()
    }, t('save_button'))));
  }
  uploadPhoto(file) {
    if (!file) return;
    this.photoUploading = true;
    m.redraw();
    uploadFile(file).then(uploaded => {
      this.photoUploading = false;
      if (uploaded) {
        this.photos.push({
          url: uploaded.attributes.url,
          type: uploaded.attributes.type
        });
      }
      m.redraw();
    }).catch(error => {
      this.photoUploading = false;
      this.errorMessage = error.response && error.response.errors && error.response.errors[0] && error.response.errors[0].detail || app.translator.trans('champis-exhibitions.forum.finding_modal.error');
      m.redraw();
    });
  }
  removePhoto(index) {
    this.photos.splice(index, 1);
  }
  canSubmit() {
    if (this.loading || this.photoUploading) return false;
    return this.undetermined || !!this.gbifId;
  }
  submit() {
    this.loading = true;
    this.errorMessage = null;
    m.redraw();
    const attributes = {
      gbifId: this.undetermined ? 0 : this.gbifId,
      cfId: this.cfId,
      label: this.label.trim() || null,
      vernacularName: this.vernacularName.trim() || null,
      photos: this.photos.map(photo => ({
        url: photo.url,
        type: photo.type
      })),
      notes: this.notes.trim() || null
    };

    // Model.save() builds its request body from `this.data.type`/`.id` plus
    // whatever is passed to *this* call - it does not also send whatever
    // relationships createRecord() might have been seeded with earlier
    // (confirmed by reading Model.ts's save() directly: it starts a fresh
    // `data` object and only fills data.relationships from this call's own
    // `attributes.relationships`). So `exhibition` has to be included here
    // too, not just at createRecord() time, on every create.
    const finding = this.attrs.finding || app.store.createRecord('champis-exhibitions-findings');
    const relationships = this.attrs.finding ? undefined : {
      exhibition: this.attrs.exhibition
    };
    finding.save(relationships ? {
      ...attributes,
      relationships
    } : attributes).then(() => {
      this.loading = false;
      this.hide();
      if (this.attrs.onsaved) this.attrs.onsaved(finding);
    }).catch(error => {
      this.loading = false;
      this.errorMessage = error && error.message || app.translator.trans('champis-exhibitions.forum.finding_modal.error');
      m.redraw();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/ExhibitionFindingEditModal', ExhibitionFindingEditModal);